import { useCallback } from 'react'
import { create } from 'zustand'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface ConsolidationStore {
    selections: Record<string, Set<string>>
    firstToggledEntry: Record<string, string>
    rangeSelected: Record<string, boolean>
    toggleEntry: (groupKey: string, entryId: string, allEntryIds: string[]) => void
    clearSelection: (groupKey: string) => void
    clearAll: () => void
}

export const useConsolidationStore = create<ConsolidationStore>()(set => ({
    selections: {},
    firstToggledEntry: {},
    rangeSelected: {},

    toggleEntry: (groupKey, entryId, allEntryIds) =>
        set(state => {
            const current = new Set(state.selections[groupKey] || [])
            const firstEntry = state.firstToggledEntry[groupKey]
            const hasRangeSelected = state.rangeSelected[groupKey] ?? false

            if (!firstEntry) {
                current.add(entryId)
                return {
                    selections: { ...state.selections, [groupKey]: current },
                    firstToggledEntry: { ...state.firstToggledEntry, [groupKey]: entryId },
                }
            }

            if (!hasRangeSelected && entryId !== firstEntry) {
                const firstIdx = allEntryIds.indexOf(firstEntry)
                const secondIdx = allEntryIds.indexOf(entryId)
                const lo = Math.min(firstIdx, secondIdx)
                const hi = Math.max(firstIdx, secondIdx)
                const rangeIds = allEntryIds.slice(lo, hi + 1)
                const newSet = new Set(rangeIds)
                return {
                    selections: { ...state.selections, [groupKey]: newSet },
                    rangeSelected: { ...state.rangeSelected, [groupKey]: true },
                }
            }

            if (current.has(entryId)) {
                current.delete(entryId)
            } else {
                current.add(entryId)
            }
            return {
                selections: { ...state.selections, [groupKey]: current },
            }
        }),

    clearSelection: groupKey =>
        set(state => {
            const { [groupKey]: _sel, ...restSelections } = state.selections
            const { [groupKey]: _first, ...restFirst } = state.firstToggledEntry
            const { [groupKey]: _range, ...restRange } = state.rangeSelected
            return {
                selections: restSelections,
                firstToggledEntry: restFirst,
                rangeSelected: restRange,
            }
        }),

    clearAll: () => set({ selections: {}, firstToggledEntry: {}, rangeSelected: {} }),
}))

const EMPTY_SET = new Set<string>()

export function useConsolidationSelection(groupKey: string) {
    const checkedIds = useConsolidationStore(state => state.selections[groupKey] ?? EMPTY_SET)
    const storeToggle = useConsolidationStore(state => state.toggleEntry)
    const storeClear = useConsolidationStore(state => state.clearSelection)

    const toggleEntry = useCallback(
        (entryId: string, allEntryIds: string[]) => storeToggle(groupKey, entryId, allEntryIds),
        [groupKey, storeToggle]
    )
    const clearSelection = useCallback(() => storeClear(groupKey), [groupKey, storeClear])
    const canConsolidate = checkedIds.size >= 2

    return { checkedIds, toggleEntry, clearSelection, canConsolidate }
}

export function useConsolidationDefaults(groupKey: string, entries: TimeEntriesExpanded[]) {
    const checkedIds = useConsolidationStore(state => state.selections[groupKey])

    if (!checkedIds || checkedIds.size < 2) return null

    const checked = entries.filter(e => checkedIds.has(e.id))
    if (checked.length < 2) return null

    checked.sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime())

    const clockIn = new Date(checked[0].clock_in)
    const clockOut = new Date(
        checked[checked.length - 1].clock_out || checked[checked.length - 1].clock_in
    )

    const locations = new Set(checked.map(e => e.location).filter(Boolean))

    return {
        clockIn,
        clockOut,
        jobId: checked[0].job,
        locationId: locations.size === 1 ? checked[0].location : '',
        entriesToDelete: checked.map(e => e.id),
    }
}
